/* BudgetSettingsProjectScreen.js — Project Budget Management
   Connected to /api/budgets/* server routes
   Tabs: Overview, Expenses, Categories, Forecast
   Features: Real budget/expense CRUD, category allocations, utilisation tracking,
   forecasting, approval workflow, export, company logo */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl,
  StatusBar, Modal, TextInput, Alert, ActivityIndicator, Dimensions, Share, Image, FlatList,
  KeyboardAvoidingView, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width: SW } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; } catch {} return c; });
const safe = r => { const d = r?.data; return d?.success ? (Array.isArray(d.data) ? d.data : d.data ? [d.data] : []) : []; };
const safeObj = r => r?.data?.success ? (r.data.data || r.data.totals || r.data.summary || {}) : {};

const fmtGBP = v => '£' + (parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

const CAT_ICONS = { materials:'hammer-wrench', labor:'account-hard-hat', labour:'account-hard-hat', equipment:'truck', permits:'file-certificate', consulting:'account-tie', subcontractor:'account-group', overhead:'office-building', contingency:'shield-alert', travel:'airplane', other:'dots-horizontal-circle' };
const CAT_COLORS = { materials:'#0e4e96', labor:'#10B981', labour:'#10B981', equipment:'#F59E0B', permits:'#8B5CF6', consulting:'#EF4444', subcontractor:'#0891B2', overhead:'#EA580C', contingency:'#64748B', travel:'#6366F1', other:'#94A3B8' };
const STATUS_MAP = { pending:{bg:'#FEF3C7',c:'#D97706',l:'Pending'}, approved:{bg:'#D1FAE5',c:'#059669',l:'Approved'}, rejected:{bg:'#FEE2E2',c:'#DC2626',l:'Rejected'}, paid:{bg:'#DBEAFE',c:'#2563EB',l:'Paid'} };

export default function BudgetSettingsProjectScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const project = route?.params?.project || route?.params || {};
  const projectId = project?.id || project?.projectId || null;
  const companyId = parseInt(project?.company_id || route?.params?.companyProfile?.id, 10) || 0;

  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');

  // API Data
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [monthly, setMonthly] = useState([]);

  // Modals
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showEditBudgetModal, setShowEditBudgetModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddCatModal, setShowAddCatModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Forms
  const [expForm, setExpForm] = useState({ category: 'materials', amount: '', description: '', vendor_name: '', date: new Date().toISOString().split('T')[0] });
  const [editBudgetAmt, setEditBudgetAmt] = useState('');
  const [catForm, setCatForm] = useState({ category_name: '', color_code: '#0e4e96' });

  // ═══════════════════════════════════════════════════════
  // API CALLS — All connected to /api/budgets/*
  // ═══════════════════════════════════════════════════════
  const fetchSummary = async () => {
    try {
      const r = await api.get('/api/budgets/summary', { params: { company_id: companyId } });
      if (r.data?.success) {
        const budgets = r.data.data || [];
        const totals = r.data.totals || {};
        // Find this project's budget or use first
        const thisBudget = projectId ? budgets.find(b => b.project_id == projectId) : budgets[0];
        setBudgetSummary({ budget: thisBudget, totals, all: budgets });
        if (thisBudget) setEditBudgetAmt(String(thisBudget.total_budget || '0'));
        return thisBudget;
      }
    } catch (e) { console.error('fetchSummary:', e.message); }
    // Fallback: use project data
    if (project?.budget || project?.total_budget) {
      const b = { total_budget: project.budget || project.total_budget || 0, spent_amount: 0, remaining_amount: project.budget || 0 };
      setBudgetSummary({ budget: b, totals: { total_budget: b.total_budget, total_spent: 0, remaining: b.total_budget } });
      setEditBudgetAmt(String(b.total_budget));
    }
    return null;
  };

  const fetchAllocations = async (budgetId) => {
    try {
      const params = { company_id: companyId };
      if (budgetId) params.budget_id = budgetId;
      const r = await api.get('/api/budgets/allocations', { params });
      if (r.data?.success) { setAllocations(r.data.data || []); return; }
    } catch {}
    setAllocations([]);
  };

  const fetchCategories = async () => {
    try {
      const r = await api.get('/api/budgets/categories', { params: { company_id: companyId } });
      if (r.data?.success) { setCategories(r.data.data || []); return; }
    } catch {}
  };

  const fetchExpenses = async (budgetId) => {
    try {
      const params = { company_id: companyId, limit: 200 };
      if (budgetId) params.budget_id = budgetId;
      if (projectId) params.project_id = projectId;
      const r = await api.get('/api/budgets/expenses', { params });
      if (r.data?.success) { setExpenses(r.data.data || []); return; }
    } catch {}
    setExpenses([]);
  };

  const fetchForecast = async () => {
    try {
      const r = await api.get('/api/budgets/forecast', { params: { company_id: companyId } });
      if (r.data?.success) { setForecast(r.data); return; }
    } catch {}
  };

  const fetchMonthly = async () => {
    try {
      const r = await api.get('/api/budgets/monthly', { params: { company_id: companyId, months: 12 } });
      if (r.data?.success) { setMonthly(r.data.data || []); return; }
    } catch {}
  };

  const loadAll = async () => {
    setIsLoading(true);
    try {
      // Company logo
      try { const c = await AsyncStorage.getItem('company_profile'); if (c) { const p = JSON.parse(c); if (p.logo) setCompanyLogo(p.logo); if (p.name) setCompanyName(p.name); } } catch {}

      const thisBudget = await fetchSummary();
      const budgetId = thisBudget?.id;
      await Promise.all([fetchAllocations(budgetId), fetchCategories(), fetchExpenses(budgetId), fetchForecast(), fetchMonthly()]);
    } catch (e) { console.error('loadAll:', e); }
    finally { setIsLoading(false); setRefreshing(false); }
  };

  useFocusEffect(useCallback(() => { loadAll(); }, []));
  const onRefresh = () => { setRefreshing(true); loadAll(); };

  // ═══════════════════════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════════════════════
  const stats = useMemo(() => {
    const b = budgetSummary?.budget || {};
    const tb = parseFloat(b.total_budget || 0);
    const ts = parseFloat(b.spent_amount || 0);
    const rem = tb - ts;
    const util = tb > 0 ? Math.min(Math.round((ts / tb) * 100), 100) : 0;

    const byCat = {};
    expenses.forEach(e => { const cat = e.category_name || e.category || 'Other'; byCat[cat] = (byCat[cat] || 0) + parseFloat(e.amount || 0); });
    const pending = expenses.filter(e => e.status === 'pending').length;

    return { totalBudget: tb, totalSpent: ts, remaining: rem, utilisation: util, byCat, pendingCount: pending, count: expenses.length };
  }, [budgetSummary, expenses]);

  // ═══════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════
  const handleAddExpense = async () => {
    if (!expForm.description?.trim()) { Alert.alert('Required', 'Enter a description'); return; }
    if (!expForm.amount || parseFloat(expForm.amount) <= 0) { Alert.alert('Required', 'Enter a valid amount'); return; }
    try {
      const cat = categories.find(c => c.category_name?.toLowerCase() === expForm.category?.toLowerCase());
      await api.post('/api/budgets/expenses', {
        budget_id: budgetSummary?.budget?.id || null,
        project_id: projectId,
        category_id: cat?.id || null,
        category: expForm.category,
        amount: parseFloat(expForm.amount),
        description: expForm.description,
        vendor_name: expForm.vendor_name,
        expense_date: expForm.date,
        company_id: companyId,
      });
      setShowAddExpenseModal(false);
      setExpForm({ category: 'materials', amount: '', description: '', vendor_name: '', date: new Date().toISOString().split('T')[0] });
      Alert.alert('Success', 'Expense added');
      loadAll();
    } catch (e) { Alert.alert('Error', e.response?.data?.error || e.message); }
  };

  const handleEditBudget = async () => {
    const amt = parseFloat(editBudgetAmt);
    if (isNaN(amt) || amt <= 0) { Alert.alert('Error', 'Enter a valid budget'); return; }
    try {
      if (budgetSummary?.budget?.id) {
        await api.put(`/api/budgets/${budgetSummary.budget.id}`, { total_budget: amt });
      } else {
        await api.post('/api/budgets/create', { project_id: projectId, project_name: project?.title || project?.name || 'Project', total_budget: amt, company_id: companyId });
      }
      setShowEditBudgetModal(false);
      Alert.alert('Success', 'Budget updated to ' + fmtGBP(amt));
      loadAll();
    } catch (e) { Alert.alert('Error', e.response?.data?.error || e.message); }
  };

  const handleApproveExpense = async (exp) => {
    try { await api.put(`/api/budgets/expenses/${exp.id}/approve`); Alert.alert('Approved'); setShowDetailModal(false); loadAll(); }
    catch (e) { Alert.alert('Error', e.message); }
  };

  const handleRejectExpense = async (exp) => {
    try { await api.put(`/api/budgets/expenses/${exp.id}/reject`, { reason: 'Rejected by manager' }); Alert.alert('Rejected'); setShowDetailModal(false); loadAll(); }
    catch (e) { Alert.alert('Error', e.message); }
  };

  const handleDeleteExpense = (exp) => {
    Alert.alert('Delete', `Delete "${exp.description}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try { await api.delete(`/api/budgets/expenses/${exp.id}`); setShowDetailModal(false); loadAll(); }
        catch (e) { Alert.alert('Error', e.message); }
      }},
    ]);
  };

  const handleAddCategory = async () => {
    if (!catForm.category_name?.trim()) { Alert.alert('Required', 'Enter category name'); return; }
    try {
      await api.post('/api/budgets/categories', { ...catForm, company_id: companyId });
      setShowAddCatModal(false); setCatForm({ category_name: '', color_code: '#0e4e96' });
      Alert.alert('Created', 'Category added');
      fetchCategories();
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const handleExport = () => {
    const lines = [`PROJECT BUDGET REPORT`, `Project: ${project?.title || project?.name || 'Project'}`, `Company: ${companyName}`, `Generated: ${fmtDate(new Date())}`, '',
      `SUMMARY`, `Total Budget: ${fmtGBP(stats.totalBudget)}`, `Spent: ${fmtGBP(stats.totalSpent)}`, `Remaining: ${fmtGBP(stats.remaining)}`, `Utilisation: ${stats.utilisation}%`, '',
      'EXPENSES',
      ...expenses.slice(0, 50).map(e => `${fmtDate(e.expense_date)} | ${e.category_name || e.category || '-'} | ${(e.description || '').substring(0, 30)} | ${fmtGBP(e.amount)} | ${e.status}`)];
    Share.share({ message: lines.join('\n'), title: 'Budget Report' });
  };

  // ═══════════════════════════════════════════════════════
  // HELPER
  // ═══════════════════════════════════════════════════════
  const getCatIcon = n => CAT_ICONS[(n || '').toLowerCase()] || 'cash';
  const getCatColor = n => CAT_COLORS[(n || '').toLowerCase()] || '#64748B';
  const getStatus = s => STATUS_MAP[s] || STATUS_MAP.pending;

  const TABS = [
    { k: 'overview', l: 'Overview', i: 'chart-box-outline' },
    { k: 'expenses', l: 'Expenses', i: 'receipt' },
    { k: 'categories', l: 'Categories', i: 'chart-pie' },
    { k: 'forecast', l: 'Forecast', i: 'trending-up' },
  ];

  // ═══════════════════════════════════════════════════════
  // RENDERS
  // ═══════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={['#0e4e96', '#1e7be2']} style={st.header}>
      <View style={st.hRow}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={st.hBtn}><Ionicons name="arrow-back" size={22} color="#FFF" /></TouchableOpacity>
        {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 8, marginLeft: 6 }} /> : null}
        <View style={{ flex: 1, marginLeft: 8 }}><Text style={st.hTitle}>{project?.title || project?.name || 'Project Budget'}</Text>
          <Text style={{ color: '#93C5FD', fontSize: 11 }}>{companyName ? `${companyName} · ` : ''}Budget Management</Text></View>
        <TouchableOpacity style={st.hBtn} onPress={handleExport}><MaterialCommunityIcons name="export-variant" size={20} color="#FFF" /></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderKPIs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.kBar} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {[{ l: 'Budget', v: fmtGBP(stats.totalBudget), c: '#0e4e96', i: 'cash-multiple' },
        { l: 'Spent', v: fmtGBP(stats.totalSpent), c: '#EF4444', i: 'cash-minus' },
        { l: 'Remaining', v: fmtGBP(stats.remaining), c: stats.remaining >= 0 ? '#10B981' : '#EF4444', i: 'cash-check' },
        { l: 'Used', v: stats.utilisation + '%', c: stats.utilisation > 80 ? '#EF4444' : stats.utilisation > 60 ? '#F59E0B' : '#10B981', i: 'gauge' },
        { l: 'Expenses', v: String(stats.count), c: '#6366F1', i: 'receipt' },
        { l: 'Pending', v: String(stats.pendingCount), c: '#D97706', i: 'clock-outline' },
      ].map((s, i) => (<View key={i} style={st.kItem}><MaterialCommunityIcons name={s.i} size={16} color={s.c} /><Text style={[st.kVal, { color: s.c }]}>{s.v}</Text><Text style={st.kLbl}>{s.l}</Text></View>))}
    </ScrollView>
  );

  const renderTabs = () => (
    <View style={st.tRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
      {TABS.map(t => (<TouchableOpacity key={t.k} style={[st.tab, activeTab === t.k && st.tabA]} onPress={() => setActiveTab(t.k)}>
        <MaterialCommunityIcons name={t.i} size={16} color={activeTab === t.k ? '#0e4e96' : '#94A3B8'} />
        <Text style={[st.tabT, activeTab === t.k && { color: '#0e4e96', fontWeight: '700' }]}>{t.l}</Text></TouchableOpacity>))}
    </ScrollView></View>
  );

  // ── Overview ──
  const renderOverview = () => (
    <View style={{ padding: 16 }}>
      <View style={st.card}><View style={st.cHead}><Text style={st.cTitle}>Budget Summary</Text>
        <TouchableOpacity style={st.eBtn} onPress={() => setShowEditBudgetModal(true)}><MaterialCommunityIcons name="pencil" size={14} color="#0e4e96" /><Text style={st.eBtnT}>Edit</Text></TouchableOpacity></View>
        {[{ l: 'Total Budget', v: fmtGBP(stats.totalBudget), c: '#1E293B' }, { l: 'Total Spent', v: fmtGBP(stats.totalSpent), c: '#EF4444' }, { l: 'Remaining', v: fmtGBP(stats.remaining), c: stats.remaining >= 0 ? '#10B981' : '#EF4444' }].map((r, i) => (
          <View key={i} style={st.sRow}><Text style={st.sLbl}>{r.l}</Text><Text style={[st.sVal, { color: r.c }]}>{r.v}</Text></View>))}
        <View style={{ marginTop: 12 }}><Text style={st.sLbl}>Utilisation: {stats.utilisation}%</Text>
          <View style={st.barBg}><View style={[st.barFl, { width: `${stats.utilisation}%`, backgroundColor: stats.utilisation > 80 ? '#EF4444' : stats.utilisation > 60 ? '#F59E0B' : '#10B981' }]} /></View>
          <Text style={{ fontSize: 11, color: '#64748B', marginTop: 4, fontStyle: 'italic' }}>{stats.utilisation < 50 ? 'Budget on track' : stats.utilisation < 75 ? 'Monitor spending' : stats.utilisation < 90 ? 'Review upcoming expenses' : 'Immediate attention needed'}</Text></View>
      </View>

      <View style={st.card}><Text style={st.cTitle}>Spending by Category</Text>
        {allocations.length > 0 ? allocations.map((a, i) => {
          const pct = parseFloat(a.utilization_percent || 0);
          return (<View key={i} style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <MaterialCommunityIcons name={getCatIcon(a.category_name)} size={14} color={a.color_code || getCatColor(a.category_name)} />
                <Text style={{ fontSize: 13, color: '#334155', marginLeft: 6 }}>{a.category_name}</Text></View>
              <Text style={{ fontSize: 13, fontWeight: '700', color: a.color_code || '#0e4e96' }}>{fmtGBP(a.spent_amount)} / {fmtGBP(a.allocated_amount)}</Text></View>
            <View style={{ height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
              <View style={{ width: `${Math.min(pct, 100)}%`, height: '100%', backgroundColor: a.color_code || getCatColor(a.category_name), borderRadius: 3 }} /></View>
          </View>);
        }) : Object.entries(stats.byCat).length > 0 ? Object.entries(stats.byCat).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
          const pct = stats.totalSpent > 0 ? Math.round((amt / stats.totalSpent) * 100) : 0;
          return (<View key={cat} style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <MaterialCommunityIcons name={getCatIcon(cat)} size={14} color={getCatColor(cat)} />
                <Text style={{ fontSize: 13, color: '#334155', marginLeft: 6 }}>{cat}</Text></View>
              <Text style={{ fontSize: 13, fontWeight: '700', color: getCatColor(cat) }}>{fmtGBP(amt)} ({pct}%)</Text></View>
            <View style={{ height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
              <View style={{ width: `${pct}%`, height: '100%', backgroundColor: getCatColor(cat), borderRadius: 3 }} /></View>
          </View>);
        }) : <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No expense data yet</Text>}
      </View>

      {monthly.length > 0 && <View style={st.card}><Text style={st.cTitle}>Monthly Spending</Text>
        {monthly.map((m, i) => (<View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
          <Text style={{ fontSize: 14, color: '#475569' }}>{m.month}</Text>
          <Text style={{ fontSize: 14, fontWeight: '700', color: '#0e4e96' }}>{fmtGBP(m.total)} ({m.count} items)</Text></View>))}
      </View>}
    </View>
  );

  // ── Expenses ──
  const renderExpenses = () => (
    <View style={{ padding: 16 }}>
      <View style={st.cHead}><Text style={st.cTitle}>Expenses ({expenses.length})</Text>
        <TouchableOpacity style={[st.eBtn, { backgroundColor: '#0e4e96' }]} onPress={() => setShowAddExpenseModal(true)}>
          <Ionicons name="add" size={16} color="#FFF" /><Text style={[st.eBtnT, { color: '#FFF' }]}>Add</Text></TouchableOpacity></View>
      {expenses.length === 0 ? (<View style={{ alignItems: 'center', padding: 40 }}><MaterialCommunityIcons name="receipt" size={48} color="#CBD5E1" />
        <Text style={{ color: '#64748B', marginTop: 12, fontSize: 15, fontWeight: '600' }}>No expenses yet</Text>
        <TouchableOpacity style={[st.eBtn, { backgroundColor: '#0e4e96', marginTop: 16, paddingHorizontal: 20, paddingVertical: 10 }]} onPress={() => setShowAddExpenseModal(true)}>
          <Ionicons name="add" size={16} color="#FFF" /><Text style={[st.eBtnT, { color: '#FFF' }]}>Add First Expense</Text></TouchableOpacity></View>
      ) : expenses.map((e, i) => {
        const catName = e.category_name || e.category || 'Other';
        const status = getStatus(e.status);
        return (<TouchableOpacity key={e.id || i} style={st.expCard} onPress={() => { setSelectedExpense(e); setShowDetailModal(true); }}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={[st.catDot, { backgroundColor: (e.color_code || getCatColor(catName)) + '20' }]}>
              <MaterialCommunityIcons name={e.category_icon || getCatIcon(catName)} size={18} color={e.color_code || getCatColor(catName)} /></View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={st.expTitle} numberOfLines={1}>{e.description || catName}</Text>
              <Text style={st.expMeta}>{catName} · {fmtDate(e.expense_date)}{e.vendor_name ? ` · ${e.vendor_name}` : ''}</Text></View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={[st.expAmt, { color: e.color_code || getCatColor(catName) }]}>{fmtGBP(e.amount)}</Text>
              <View style={[st.badge, { backgroundColor: status.bg }]}><Text style={[st.badgeT, { color: status.c }]}>{status.l}</Text></View></View>
          </View>
        </TouchableOpacity>);
      })}
    </View>
  );

  // ── Categories ──
  const renderCategories = () => (
    <View style={{ padding: 16 }}>
      <View style={st.cHead}><Text style={st.cTitle}>Budget Categories</Text>
        <TouchableOpacity style={[st.eBtn, { backgroundColor: '#0e4e96' }]} onPress={() => setShowAddCatModal(true)}>
          <Ionicons name="add" size={16} color="#FFF" /><Text style={[st.eBtnT, { color: '#FFF' }]}>Add</Text></TouchableOpacity></View>
      {categories.length > 0 ? categories.map((c, i) => {
        const alloc = allocations.find(a => a.category_id === c.id);
        const spent = parseFloat(alloc?.spent_amount || 0);
        const allocated = parseFloat(alloc?.allocated_amount || 0);
        const pct = allocated > 0 ? Math.min(Math.round((spent / allocated) * 100), 100) : 0;
        return (<View key={c.id || i} style={st.catCard}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={[st.catDot, { backgroundColor: (c.color_code || '#0e4e96') + '20' }]}>
                <MaterialCommunityIcons name={c.icon || getCatIcon(c.category_name)} size={16} color={c.color_code || '#0e4e96'} /></View>
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#1E293B', marginLeft: 8 }}>{c.category_name}</Text></View>
            {allocated > 0 && <Text style={{ fontSize: 14, fontWeight: '700', color: '#0e4e96' }}>{pct}%</Text>}
          </View>
          {allocated > 0 && <><View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 6, marginBottom: 4 }}>
            <Text style={{ fontSize: 12, color: '#EF4444' }}>Spent: {fmtGBP(spent)}</Text>
            <Text style={{ fontSize: 12, color: '#64748B' }}>Allocated: {fmtGBP(allocated)}</Text></View>
            <View style={{ height: 6, backgroundColor: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
              <View style={{ width: `${pct}%`, height: '100%', backgroundColor: c.color_code || '#0e4e96', borderRadius: 3 }} /></View></>}
          {c.description ? <Text style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>{c.description}</Text> : null}
        </View>);
      }) : <View style={{ alignItems: 'center', padding: 40 }}><MaterialCommunityIcons name="chart-pie" size={48} color="#CBD5E1" />
        <Text style={{ color: '#64748B', marginTop: 12 }}>No categories yet</Text></View>}
    </View>
  );

  // ── Forecast ──
  const renderForecast = () => {
    const f = forecast || {};
    return (
      <View style={{ padding: 16 }}>
        <View style={st.card}><Text style={st.cTitle}>Budget Forecast</Text>
          {[{ l: 'Avg Monthly Spend', v: fmtGBP(f.avg_monthly_spend || 0), c: '#0e4e96' },
            { l: 'Months Until Depleted', v: f.months_until_depleted || 'N/A', c: parseFloat(f.months_until_depleted || 99) < 3 ? '#EF4444' : '#10B981' },
            { l: 'Projected Annual', v: fmtGBP(f.projected_annual || 0), c: f.over_budget_risk ? '#EF4444' : '#10B981' },
            { l: 'Remaining Budget', v: fmtGBP(f.remaining || stats.remaining), c: (f.remaining || stats.remaining) >= 0 ? '#10B981' : '#EF4444' },
          ].map((r, i) => (<View key={i} style={st.sRow}><Text style={st.sLbl}>{r.l}</Text><Text style={[st.sVal, { color: r.c }]}>{r.v}</Text></View>))}
          <View style={[st.alertBox, { backgroundColor: f.over_budget_risk ? '#FEE2E2' : '#D1FAE5', borderColor: f.over_budget_risk ? '#FECACA' : '#BBF7D0' }]}>
            <MaterialCommunityIcons name={f.over_budget_risk ? 'alert' : 'check-circle'} size={20} color={f.over_budget_risk ? '#DC2626' : '#059669'} />
            <Text style={{ flex: 1, marginLeft: 8, color: f.over_budget_risk ? '#DC2626' : '#059669', fontSize: 13 }}>
              {f.over_budget_risk ? 'At current rate, project will exceed budget' : 'Budget on track at current spending rate'}</Text></View>
        </View>
        {(f.forecast || []).length > 0 && <View style={st.card}><Text style={st.cTitle}>3-Month Forecast</Text>
          {f.forecast.map((m, i) => (<View key={i} style={st.sRow}><Text style={st.sLbl}>{m.month}</Text>
            <Text style={[st.sVal, { color: m.cumulative > stats.totalBudget ? '#EF4444' : '#0e4e96' }]}>Predicted: {fmtGBP(m.predicted)} (Total: {fmtGBP(m.cumulative)})</Text></View>))}
        </View>}
        {(f.historical || []).length > 0 && <View style={st.card}><Text style={st.cTitle}>Historical Spending</Text>
          {f.historical.map((m, i) => (<View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
            <Text style={{ fontSize: 14, color: '#475569' }}>{m.month}</Text>
            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0e4e96' }}>{fmtGBP(m.total)}</Text></View>))}
        </View>}
      </View>
    );
  };

  const renderContent = () => { switch (activeTab) {
    case 'overview': return renderOverview();
    case 'expenses': return renderExpenses();
    case 'categories': return renderCategories();
    case 'forecast': return renderForecast();
    default: return renderOverview();
  }};

  // ── Modals ──
  const renderAddExpenseModal = () => (
    <Modal visible={showAddExpenseModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddExpenseModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={st.mH}><TouchableOpacity onPress={() => setShowAddExpenseModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
          <Text style={st.mTitle}>Add Expense</Text><View style={{ width: 24 }} /></View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={st.fLbl}>Category</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
              {(categories.length > 0 ? categories.map(c => ({ id: c.category_name?.toLowerCase(), name: c.category_name, color: c.color_code })) :
                Object.entries(CAT_ICONS).map(([id, icon]) => ({ id, name: id.charAt(0).toUpperCase() + id.slice(1), color: CAT_COLORS[id] }))
              ).map(c => (
                <TouchableOpacity key={c.id} style={[st.chip, expForm.category === c.id && { backgroundColor: c.color || '#0e4e96', borderColor: c.color || '#0e4e96' }]}
                  onPress={() => setExpForm(p => ({ ...p, category: c.id }))}>
                  <MaterialCommunityIcons name={getCatIcon(c.id)} size={14} color={expForm.category === c.id ? '#FFF' : c.color || '#64748B'} />
                  <Text style={[st.chipT, expForm.category === c.id && { color: '#FFF' }]}>{c.name}</Text></TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={st.fLbl}>Amount (£)</Text>
            <TextInput style={st.fInput} value={expForm.amount} onChangeText={t => setExpForm(p => ({ ...p, amount: t }))} placeholder="0.00" keyboardType="numeric" />
            <Text style={st.fLbl}>Description</Text>
            <TextInput style={[st.fInput, { height: 80, textAlignVertical: 'top' }]} value={expForm.description} onChangeText={t => setExpForm(p => ({ ...p, description: t }))} placeholder="What was this expense for?" multiline />
            <Text style={st.fLbl}>Vendor / Supplier</Text>
            <TextInput style={st.fInput} value={expForm.vendor_name} onChangeText={t => setExpForm(p => ({ ...p, vendor_name: t }))} placeholder="Vendor name (optional)" />
            <Text style={st.fLbl}>Date</Text>
            <TextInput style={st.fInput} value={expForm.date} onChangeText={t => setExpForm(p => ({ ...p, date: t }))} placeholder="YYYY-MM-DD" />
            <TouchableOpacity style={st.subBtn} onPress={handleAddExpense}>
              <MaterialCommunityIcons name="check" size={18} color="#FFF" /><Text style={st.subBtnT}>Add Expense</Text></TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  const renderEditBudgetModal = () => (
    <Modal visible={showEditBudgetModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowEditBudgetModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={st.mH}><TouchableOpacity onPress={() => setShowEditBudgetModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
          <Text style={st.mTitle}>{budgetSummary?.budget?.id ? 'Edit Budget' : 'Create Budget'}</Text><View style={{ width: 24 }} /></View>
        <View style={{ padding: 16 }}>
          <Text style={st.fLbl}>Total Budget (£)</Text>
          <TextInput style={st.fInput} value={editBudgetAmt} onChangeText={setEditBudgetAmt} placeholder="0" keyboardType="numeric" />
          <Text style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>Current: {fmtGBP(stats.totalBudget)}</Text>
          <TouchableOpacity style={st.subBtn} onPress={handleEditBudget}>
            <MaterialCommunityIcons name="check" size={18} color="#FFF" /><Text style={st.subBtnT}>{budgetSummary?.budget?.id ? 'Update' : 'Create'} Budget</Text></TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderAddCatModal = () => (
    <Modal visible={showAddCatModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddCatModal(false)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={st.mH}><TouchableOpacity onPress={() => setShowAddCatModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
          <Text style={st.mTitle}>New Category</Text><View style={{ width: 24 }} /></View>
        <View style={{ padding: 16 }}>
          <Text style={st.fLbl}>Category Name</Text>
          <TextInput style={st.fInput} value={catForm.category_name} onChangeText={t => setCatForm(p => ({ ...p, category_name: t }))} placeholder="e.g. Materials, Labour" />
          <Text style={st.fLbl}>Color (hex)</Text>
          <TextInput style={st.fInput} value={catForm.color_code} onChangeText={t => setCatForm(p => ({ ...p, color_code: t }))} placeholder="#0e4e96" />
          <TouchableOpacity style={st.subBtn} onPress={handleAddCategory}>
            <MaterialCommunityIcons name="check" size={18} color="#FFF" /><Text style={st.subBtnT}>Create Category</Text></TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  const renderDetailModal = () => {
    if (!selectedExpense) return null;
    const catName = selectedExpense.category_name || selectedExpense.category || 'Other';
    const status = getStatus(selectedExpense.status);
    return (
      <Modal visible={showDetailModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetailModal(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
          <View style={st.mH}><TouchableOpacity onPress={() => setShowDetailModal(false)}><Ionicons name="close" size={24} color="#64748B" /></TouchableOpacity>
            <Text style={st.mTitle}>Expense Details</Text><View style={{ width: 24 }} /></View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <View style={st.card}>
              {[{ l: 'Description', v: selectedExpense.description }, { l: 'Amount', v: fmtGBP(selectedExpense.amount) }, { l: 'Category', v: catName },
                { l: 'Date', v: fmtDate(selectedExpense.expense_date) }, { l: 'Vendor', v: selectedExpense.vendor_name }, { l: 'Status', v: status.l },
                { l: 'Notes', v: selectedExpense.notes },
              ].filter(r => r.v).map((r, i) => (<View key={i} style={st.sRow}><Text style={st.sLbl}>{r.l}</Text><Text style={st.sVal}>{r.v}</Text></View>))}
            </View>
            <View style={{ gap: 8 }}>
              {selectedExpense.status === 'pending' && <>
                <TouchableOpacity style={[st.subBtn, { backgroundColor: '#10B981' }]} onPress={() => handleApproveExpense(selectedExpense)}>
                  <MaterialCommunityIcons name="check" size={18} color="#FFF" /><Text style={st.subBtnT}>Approve</Text></TouchableOpacity>
                <TouchableOpacity style={[st.subBtn, { backgroundColor: '#EF4444' }]} onPress={() => handleRejectExpense(selectedExpense)}>
                  <MaterialCommunityIcons name="close" size={18} color="#FFF" /><Text style={st.subBtnT}>Reject</Text></TouchableOpacity></>}
              <TouchableOpacity style={[st.subBtn, { backgroundColor: '#64748B' }]} onPress={() => handleDeleteExpense(selectedExpense)}>
                <MaterialCommunityIcons name="delete" size={18} color="#FFF" /><Text style={st.subBtnT}>Delete</Text></TouchableOpacity>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // ═══════════════════════════════════════════════════════
  if (isLoading) return (<SafeAreaView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
    <StatusBar barStyle="light-content" /><ActivityIndicator size="large" color="#0e4e96" /><Text style={{ color: '#64748B', marginTop: 12 }}>Loading budget...</Text></SafeAreaView>);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}{renderKPIs()}{renderTabs()}
      <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />} showsVerticalScrollIndicator={false}>
        {renderContent()}<View style={{ height: 80 }} /></ScrollView>
      <TouchableOpacity style={st.fab} onPress={() => setShowAddExpenseModal(true)}>
        <MaterialCommunityIcons name="plus" size={24} color="#FFF" /></TouchableOpacity>
      {renderAddExpenseModal()}{renderEditBudgetModal()}{renderAddCatModal()}{renderDetailModal()}
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  header:{paddingTop:8,paddingBottom:12,paddingHorizontal:16},hRow:{flexDirection:'row',alignItems:'center'},hBtn:{width:36,height:36,borderRadius:12,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},hTitle:{fontSize:18,fontWeight:'800',color:'#FFF'},
  kBar:{flexGrow:0,backgroundColor:'#FFF',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#E2E8F0'},kItem:{alignItems:'center',marginRight:14,minWidth:65},kVal:{fontSize:14,fontWeight:'800',marginTop:2},kLbl:{fontSize:10,color:'#94A3B8',marginTop:1},
  tRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E2E8F0'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:12,paddingVertical:10,borderRadius:14,marginRight:2},tabA:{backgroundColor:'#EFF6FF'},tabT:{fontSize:12,color:'#94A3B8',marginLeft:4,fontWeight:'500'},
  card:{backgroundColor:'#FFF',borderRadius:12,padding:16,marginBottom:12,borderWidth:1,borderColor:'#E2E8F0'},cHead:{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:12},cTitle:{fontSize:16,fontWeight:'800',color:'#0F172A',marginBottom:8},
  eBtn:{flexDirection:'row',alignItems:'center',paddingHorizontal:10,paddingVertical:5,backgroundColor:'#F1F5F9',borderRadius:8,gap:4},eBtnT:{fontSize:13,color:'#0e4e96',fontWeight:'600'},
  sRow:{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#F1F5F9'},sLbl:{fontSize:14,color:'#64748B',fontWeight:'500'},sVal:{fontSize:15,fontWeight:'700',color:'#1E293B',flexShrink:1,textAlign:'right',maxWidth:'60%'},
  barBg:{height:8,backgroundColor:'#E2E8F0',borderRadius:4,marginTop:6,overflow:'hidden'},barFl:{height:'100%',borderRadius:4},
  expCard:{backgroundColor:'#FFF',borderRadius:10,padding:12,marginBottom:6,borderWidth:1,borderColor:'#E2E8F0'},catDot:{width:38,height:38,borderRadius:10,justifyContent:'center',alignItems:'center'},
  expTitle:{fontSize:14,fontWeight:'600',color:'#1E293B'},expMeta:{fontSize:11,color:'#94A3B8',marginTop:1},expAmt:{fontSize:15,fontWeight:'800'},
  badge:{paddingHorizontal:6,paddingVertical:2,borderRadius:6,marginTop:2},badgeT:{fontSize:10,fontWeight:'600'},
  catCard:{backgroundColor:'#FFF',borderRadius:10,padding:14,marginBottom:8,borderWidth:1,borderColor:'#E2E8F0'},
  alertBox:{flexDirection:'row',alignItems:'center',padding:12,borderRadius:10,borderWidth:1,marginTop:12},
  chip:{flexDirection:'row',alignItems:'center',paddingHorizontal:10,paddingVertical:7,borderRadius:14,borderWidth:1.5,borderColor:'#E2E8F0',marginRight:6},chipT:{fontSize:12,color:'#64748B',fontWeight:'600',marginLeft:4},
  fab:{position:'absolute',bottom:24,right:20,width:56,height:56,borderRadius:16,backgroundColor:'#0e4e96',alignItems:'center',justifyContent:'center',elevation:8,shadowColor:'#0e4e96',shadowOffset:{width:0,height:4},shadowOpacity:0.3,shadowRadius:8},
  mH:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E2E8F0',backgroundColor:'#FFF'},mTitle:{fontSize:17,fontWeight:'700',color:'#0F172A',flex:1,textAlign:'center'},
  fLbl:{fontSize:13,fontWeight:'600',color:'#334155',marginBottom:6,marginTop:10},fInput:{backgroundColor:'#FFF',borderWidth:1.5,borderColor:'#E2E8F0',borderRadius:10,paddingHorizontal:14,paddingVertical:12,fontSize:15,color:'#0F172A',marginBottom:4},
  subBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#0e4e96',borderRadius:12,paddingVertical:14,marginTop:16,gap:6},subBtnT:{color:'#FFF',fontSize:16,fontWeight:'700'},
});
